import React, { useEffect, useRef, useState } from 'react'
import { debounce as debounceFn } from 'lodash'
import {
  Box,
  FormLabel,
  TextField,
  FormControl,
  InputAdornment,
} from '@mui/material'
import { CommonLoader } from './CommonLoader'
import { getLabel, getComputedWidth, isValueAllowed } from './utils'

export const CommonTextField = ({
  id,
  label,
  value,
  error,
  width,
  readOnly,
  helperText,
  labelRight,
  fullWidth = true,
  required = false,
  debounce = false,
  debounceTime = 400,
  restrictedValues = null,
  loading = false,
  setValue,
  onChange,
  onBlur,
  inputProps,
  ...muiProps
}) => {
  const isValidInput = React.useRef(true)
  const [localValue, setLocalValue] = useState(
    !isValueAllowed(value, restrictedValues) ? '' : value
  )

  useEffect(() => {
    if (isValueAllowed(value, restrictedValues)) setLocalValue(value)
    else setLocalValue('')
  }, [value])

  const handleChange = newValue => {
    if (setValue) setValue(newValue)
    if (onChange) onChange(newValue)
  }

  const handleDebouncedChange = useRef(
    debounceFn(newValue => {
      handleChange(newValue)
    }, debounceTime)
  ).current

  const handleInput = event => {
    const newValue = event.target.value
    isValidInput.current = isValueAllowed(newValue, restrictedValues)
    if (!isValidInput.current) return

    setLocalValue(newValue)
    if (debounce) handleDebouncedChange(newValue)
    else handleChange(newValue)
  }

  const handleBlur = () => {
    if (onBlur) onBlur(localValue)
  }

  // When `error` is boolean true, `error || helperText` would yield `true` (not the message).
  const helperDisplay =
    typeof error === 'string' && error.trim() ? error : (helperText ?? '')
  const errorBoolean =
    typeof error === 'boolean'
      ? error
      : typeof error === 'string'
        ? !!error.trim()
        : !!error

  return (
    <Box data-testid={id} sx={{ width: getComputedWidth(width, fullWidth) }}>
      <FormControl fullWidth error={errorBoolean}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.25,
          }}
        >
          <FormLabel sx={{ mb: 0 }}>{getLabel(label, required)}</FormLabel>
          {labelRight ? <Box sx={{ ml: 1 }}>{labelRight}</Box> : null}
        </Box>
        <TextField
          {...muiProps}
          id={id}
          value={localValue}
          onChange={handleInput}
          onBlur={handleBlur}
          error={errorBoolean}
          helperText={helperDisplay}
          fullWidth={fullWidth}
          disabled={readOnly || muiProps.disabled}
          slotProps={{
            ...muiProps.slotProps,
            input: {
              ...muiProps.slotProps?.input,
              endAdornment: (
                <>
                  {muiProps.slotProps?.input?.endAdornment}
                  {loading ? (
                    <InputAdornment position='end'>
                      <CommonLoader size={16} thickness={4} />
                    </InputAdornment>
                  ) : null}
                </>
              ),
            },
          }}
          inputProps={{
            autoComplete: 'new-password',
            form: {
              autoComplete: 'off',
            },
            ...inputProps,
          }}
          sx={{
            ...(readOnly && {
              '& .MuiInputBase-input': {
                color: theme => `${theme.palette.text.primary} !important`,
                WebkitTextFillColor: theme =>
                  `${theme.palette.text.primary} !important`,
              },
            }),
          }}
        />
      </FormControl>
    </Box>
  )
}
